import { ReactElement, useRef, useState } from 'react';

import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import {
  UP_GLOBAL_CONTEXTS,
  UP_LEARNER_PROFILE_ATTRIBUTES,
  UP_PAGE_CHAR_LIMIT,
  UP_PROMPT_NAME_MAP,
  UP_TRANSDISCIPLINARY_THEMES,
  UnitPlanPromptNames,
} from '@lessonPlanner/constants';

import { Button } from '../components/Button';
import {
  MultiSelectField,
  SelectField,
  TextAreaField,
  TextField,
} from '../components/form-fields';
import GenerateButton from '../components/GenerateButton';
import GenericBanner from '../components/GenericBanner';
import MainContent from '../components/MainContent';
import { GradeLevelDropdown } from '../components/Select';
import { PAGE_BUTTON_TEXT } from '../constants/buttonConstants';
import { UNIT_PLAN_PAGE_DESCRIPTION } from '../constants/unitPlan';
import { useGeneratePause } from '../context/generate/provider';
import { PageKey, UnitPlanProperties } from '../enums/pageKey';
import useAutosizeTextArea from '../hooks/useAutoSizeTextArea';
import SidebarLayout from '../layouts/SidebarLayout';
import UnitPlannerSchema from '../schemas/UnitPlannerSchema';
import { unitPlanProperties } from '../types/page';
import { getFirstGradeLevelForCurriculum } from '../utils/curriculumHelpers';
import { getLocalStorageItem } from '../utils/helper';
import { parseJSON } from '../utils/parseJson';

export default function UnitPlan(): JSX.Element {
  const defaultValues = {
    gradeLevel: getLocalStorageItem(UnitPlanProperties.gradeLevel),
    subject: getLocalStorageItem(UnitPlanProperties.subject),
    topic: getLocalStorageItem(UnitPlanProperties.topic),
    format: getValidFormatValue(getLocalStorageItem(UnitPlanProperties.format)),
    theme: getLocalStorageItem(UnitPlanProperties.theme),
    context: getLocalStorageItem(UnitPlanProperties.context),
    attributes: parseJSON(
      getLocalStorageItem(UnitPlanProperties.attributes),
      [] as string[],
    ),
    goals: getLocalStorageItem(UnitPlanProperties.goals),
  };

  const unitPlanForm = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(UnitPlannerSchema),
  });

  const {
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = unitPlanForm;

  const defaultPremiumCreditCost = parseInt(
    process.env.NEXT_PUBLIC_GENERATE_PREMIUM_UNIT_PLAN_COST,
  );

  const topic = watch('topic');
  const gradeLevel = watch('gradeLevel');
  const subject = watch('subject');
  const format = watch('format');
  const theme = watch('theme');
  const context = watch('context');
  const attributes = watch('attributes');
  const goals = watch('goals');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(textAreaRef.current, topic);

  const [showTheme, setShowTheme] = useState(false);
  const [showAttributes, setShowAttributes] = useState(false);
  const [showGoals, setShowGoals] = useState(false);

  const formContents = {
    [UnitPlanProperties.topic]: topic,
    [UnitPlanProperties.gradeLevel]: gradeLevel,
    [UnitPlanProperties.subject]: subject,
    [UnitPlanProperties.format]: format,
    [UnitPlanProperties.context]: context,
    [UnitPlanProperties.theme]: theme,
    [UnitPlanProperties.attributes]: attributes,
    [UnitPlanProperties.goals]: goals,
  };

  async function triggerValidation(fieldName) {
    await trigger(fieldName);
  }

  async function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
  }

  async function customFormValidator() {
    await trigger();
  }

  function getValidFormatValue(value: string) {
    const isValid = (Object.values(UnitPlanPromptNames) as string[]).includes(
      value,
    );

    if (!isValid) {
      return UnitPlanPromptNames.PYP;
    }

    return value as UnitPlanPromptNames;
  }

  const handleMultipleSelectChange = (value) => {
    setValue('attributes', value);
  };

  const isDP = format === UnitPlanPromptNames.DP;
  const isMYP = format === UnitPlanPromptNames.MYP;
  const fieldConfig = {
    label: isMYP ? 'Context' : 'Transdisciplinary Theme',
    id: isMYP ? 'context' : 'theme',
    value: isMYP ? context : theme,
    error: isMYP ? !!errors.context : !!errors.theme,
    errorMessage: isMYP
      ? 'Please select the context'
      : 'Please select the theme',
  };

  const hasErrors = Object.entries(errors).length > 0;

  const { pausedButtonText, isGeneratePaused } = useGeneratePause();
  const buttonText = isGeneratePaused
    ? pausedButtonText
    : PAGE_BUTTON_TEXT[PageKey.unitPlan];

  const isGenerateButtonDisabled = isLoading || hasErrors || isGeneratePaused;

  const banner = (
    <GenericBanner content={UNIT_PLAN_PAGE_DESCRIPTION} title="Unit Planning" />
  );

  return (
    <MainContent
      banner={banner}
      creditCost={defaultPremiumCreditCost}
      formContents={formContents as unitPlanProperties}
      pageKey={PageKey.unitPlan}
      setIsLoading={setIsLoading}
    >
      <SelectField
        className="col-span-full"
        error={!!errors.format}
        errorMessage={errors?.format?.message}
        id="format"
        label="Curriculum"
        name="format"
        value={format}
        onBlur={() => triggerValidation('format')}
        onChange={(value) => {
          updateFieldValue('format', value);
          const firstGradeLevelOption = getFirstGradeLevelForCurriculum(
            value as UnitPlanPromptNames,
          );
          updateFieldValue('gradeLevel', firstGradeLevelOption);
        }}
      >
        {Object.values(UnitPlanPromptNames).map((promptName) => {
          return (
            <option key={promptName} value={promptName}>
              {UP_PROMPT_NAME_MAP[promptName]}
            </option>
          );
        })}
      </SelectField>
      <GradeLevelDropdown
        curriculum={format as UnitPlanPromptNames}
        error={!!errors.gradeLevel}
        errorMessage={!!errors.gradeLevel && 'Please select the Grade Level'}
        isCurriculumBased={true}
        value={gradeLevel}
        onBlur={() => triggerValidation('gradeLevel')}
        onChange={(value) => updateFieldValue('gradeLevel', value)}
      />
      <TextField
        className="col-span-full"
        errorMessage={!!errors.subject && 'Please enter the Subject'}
        id="subject"
        isMaxLengthReached={subject?.length >= UP_PAGE_CHAR_LIMIT}
        label="Subject"
        maxLength={UP_PAGE_CHAR_LIMIT}
        name="subject"
        placeholder={'Input the subject for this unit plan'}
        type="subject"
        value={subject}
        onBlur={() => {
          updateFieldValue('subject', subject?.trim() || '');
          triggerValidation('subject');
        }}
        onChange={(e) => {
          updateFieldValue('subject', e.target.value);
          triggerValidation('subject');
        }}
      />

      <TextAreaField
        showSubLabel
        className="col-span-full"
        error={!!errors.topic}
        errorMessage={!!errors.topic && 'Please enter the Topic'}
        id="topic"
        isMaxLengthReached={topic?.length >= UP_PAGE_CHAR_LIMIT}
        label="Topic"
        maxLength={UP_PAGE_CHAR_LIMIT}
        name="topic"
        placeholder="Input the topic for this unit plan"
        rows={2}
        subLabel="What is the topic of this unit plan?"
        textAreaRef={textAreaRef}
        type="topic"
        value={topic}
        onBlur={() => {
          updateFieldValue('topic', topic?.trim() || '');
          triggerValidation('topic');
        }}
        onChange={(e) => {
          updateFieldValue('topic', e.target.value);
          triggerValidation('topic');
        }}
      />

      {!showTheme && format !== 'DP' && (
        <Button
          className="col-span-full flex items-center font-medium text-blue-600 hover:text-blue-700"
          type="button"
          onClick={() => {
            setShowTheme(true);
            updateFieldValue('theme', UP_TRANSDISCIPLINARY_THEMES[0]);
          }}
        >
          <PlusIcon className="mr-2 inline h-4 w-4" />
          Add {isMYP ? 'Context' : 'Transdisciplinary Theme'}
        </Button>
      )}

      {showTheme && !isDP && (
        <div className="col-span-full flex items-center gap-2">
          <div className="flex-grow">
            <SelectField
              className="w-full"
              error={fieldConfig.error}
              errorMessage={fieldConfig.errorMessage}
              id={fieldConfig.id}
              label={fieldConfig.label}
              name={fieldConfig.id}
              value={fieldConfig.value}
              onBlur={() => triggerValidation(fieldConfig.id)}
              onChange={(value) => updateFieldValue(fieldConfig.id, value)}
            >
              {(isMYP ? UP_GLOBAL_CONTEXTS : UP_TRANSDISCIPLINARY_THEMES).map(
                (option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ),
              )}
            </SelectField>
          </div>

          <Button
            className="mt-6 inline-flex bg-white pl-2"
            type="button"
            onClick={() => {
              setShowTheme(false);
              updateFieldValue(
                isMYP ? 'context' : 'theme',
                isMYP ? UP_TRANSDISCIPLINARY_THEMES[0] : UP_GLOBAL_CONTEXTS[0],
              );
            }}
          >
            <TrashIcon className="h-6 w-6" />
          </Button>
        </div>
      )}

      {!showAttributes && (
        <Button
          className="col-span-full flex items-center font-medium text-blue-600 hover:text-blue-700"
          type="button"
          onClick={() => setShowAttributes(true)}
        >
          <PlusIcon className="mr-2 inline h-4 w-4" />
          Add Learner Profile Attributes
        </Button>
      )}

      {showAttributes && (
        <div className="items-enter col-span-full flex gap-2">
          <div className="flex-grow">
            <MultiSelectField
              className="w-full"
              error={!!errors.attributes}
              id="attributes"
              label="Learner Profile Attributes"
              maxSelections={3}
              options={UP_LEARNER_PROFILE_ATTRIBUTES}
              value={attributes || []}
              errorMessage={
                !!errors.attributes && 'Please select a maximum of 3 attributes'
              }
              onChange={handleMultipleSelectChange}
            />
          </div>

          <Button
            className="mt-6 inline-flex bg-white pl-2"
            type="button"
            onClick={() => {
              setShowAttributes(false);
              updateFieldValue('attributes', []);
            }}
          >
            <TrashIcon className="h-6 w-6" />
          </Button>
        </div>
      )}

      {!showGoals && (
        <Button
          className="col-span-full flex items-center font-medium text-blue-600 hover:text-blue-700"
          type="button"
          onClick={() => setShowGoals(true)}
        >
          <PlusIcon className="mr-2 inline h-4 w-4" />
          Add Learning Goals
        </Button>
      )}

      {showGoals && (
        <div className="items-enter col-span-full flex gap-2">
          <div className="flex-grow">
            <TextField
              className="w-full"
              errorMessage={!!errors.goals && 'Please enter the Goals'}
              id="goals"
              label="Learning Goals"
              maxLength={UP_PAGE_CHAR_LIMIT}
              name="goals"
              placeholder={'Input the goals for this unit plan'}
              type="goals"
              value={goals}
              onBlur={() => triggerValidation('goals')}
              onChange={(e) => updateFieldValue('goals', e.target.value)}
            />
          </div>

          <Button
            className="mt-6 inline-flex bg-white pl-2"
            type="button"
            onClick={() => {
              setShowGoals(false);
              updateFieldValue('goals', '');
            }}
          >
            <TrashIcon className="h-6 w-6" />
          </Button>
        </div>
      )}

      <div className="col-span-full">
        <div className="flex flex-col">
          <GenerateButton
            isPremium
            buttonText={buttonText}
            creditCost={defaultPremiumCreditCost}
            disabled={isGenerateButtonDisabled}
            onClick={customFormValidator}
          />
        </div>
      </div>
    </MainContent>
  );
}

UnitPlan.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
